<template>
  <div
    class="file-drop-zone"
    :class="{ 'is-dragging': isDragging, 'is-active': hasFiles }"
    @dragenter.prevent="handleDragEnter"
    @dragleave.prevent="handleDragLeave"
    @dragover.prevent
    @drop.prevent="handleDrop"
    @click="triggerFileInput"
  >
    <input
      ref="fileInput"
      type="file"
      multiple
      class="file-input"
      :accept="accept"
      @change="handleFileSelect"
    />

    <div class="drop-zone-content">
      <div class="drop-zone-icon">
        <span class="icon is-large">
          <font-awesome-icon
            :icon="isDragging ? ['fas', 'cloud-upload'] : ['fad', 'cloud-upload']"
            size="4x"
          />
        </span>
      </div>
      <h3 class="drop-zone-title">
        {{ isDragging ? 'Drop files here' : 'Drag & drop files here' }}
      </h3>
      <p class="drop-zone-subtitle">
        or <span class="has-text-primary has-text-weight-semibold">browse</span> to select files
      </p>
      <p class="drop-zone-hint">
        {{ acceptHint }}
      </p>
    </div>
  </div>
</template>

<script setup>
import { ref, computed } from "vue";

const props = defineProps({
  accept: {
    type: String,
    default: "*",
  },
  assetType: {
    type: String,
    default: null,
  },
});

const emit = defineEmits(["files-selected"]);

const fileInput = ref(null);
const isDragging = ref(false);
const hasFiles = ref(false);
let dragCounter = 0;

// Hint text based on asset type
const assetTypeHints = {
  videos: "Accepts MP4 video files",
  social: "Accepts MP4 video files",
  photography: "Accepts JPG, PNG, WebP, HEIC, and other image formats",
  graphics: "Accepts image files, SVG, AI, PSD, EPS, PDF, and other design files",
  audio: "Accepts MP3, WAV, AAC, FLAC, and other audio formats",
  "brand assets": "Accepts AI, PSD, SVG, PDF, fonts, and other design files",
  documents: "Accepts PDF, Word, Excel, PowerPoint, and text files",
  other: "Accepts any file type",
};

const acceptHint = computed(() => {
  if (props.assetType && assetTypeHints[props.assetType]) {
    return assetTypeHints[props.assetType];
  }
  return "Supports images, videos, audio, documents, and more";
});

function triggerFileInput() {
  fileInput.value?.click();
}

function handleFileSelect(event) {
  const files = event.target.files;
  if (files && files.length > 0) {
    hasFiles.value = true;
    emit("files-selected", files);
    // Reset the input so the same files can be selected again
    event.target.value = "";
  }
}

function handleDragEnter() {
  dragCounter++;
  isDragging.value = true;
}

function handleDragLeave() {
  dragCounter--;
  if (dragCounter === 0) {
    isDragging.value = false;
  }
}

function handleDrop(event) {
  dragCounter = 0;
  isDragging.value = false;

  const files = event.dataTransfer?.files;
  if (files && files.length > 0) {
    hasFiles.value = true;
    emit("files-selected", files);
  }
}
</script>

<style lang="scss" scoped>
.file-drop-zone {
  border: 2px dashed var(--bulma-grey-lighter);
  border-radius: 12px;
  padding: 3rem 2rem;
  text-align: center;
  cursor: pointer;
  transition: all 0.2s ease;
  background: var(--bulma-scheme-main-bis);
  position: relative;

  .file-input {
    display: none;
  }

  &:hover {
    border-color: var(--bulma-primary);
    background: var(--bulma-primary-light);

    .drop-zone-icon {
      color: var(--bulma-primary);
      transform: scale(1.05);
    }
  }

  &.is-dragging {
    border-color: var(--bulma-primary);
    border-style: solid;
    background: var(--bulma-primary-light);

    .drop-zone-icon {
      color: var(--bulma-primary);
      transform: scale(1.1);
    }
  }

  &.is-active {
    border-color: var(--bulma-primary);

    .drop-zone-icon {
      color: var(--bulma-primary);
    }
  }

  .drop-zone-content {
    pointer-events: none;
  }

  .drop-zone-icon {
    color: var(--bulma-grey-light);
    margin-bottom: 1rem;
    transition: all 0.2s ease;
  }

  .drop-zone-title {
    font-size: 1.25rem;
    font-weight: 600;
    color: var(--bulma-text-strong);
    margin-bottom: 0.5rem;
  }

  .drop-zone-subtitle {
    color: var(--bulma-grey);
    margin-bottom: 0.5rem;
  }

  .drop-zone-hint {
    font-size: 0.875rem;
    color: var(--bulma-grey-light);
  }
}

@media screen and (max-width: 768px) {
  .file-drop-zone {
    padding: 1.5rem 1rem;

    .drop-zone-icon {
      margin-bottom: 0.5rem;

      .icon.is-large {
        width: 2rem;
        height: 2rem;
      }

      :deep(svg) {
        width: 2rem;
        height: 2rem;
      }
    }

    .drop-zone-title {
      font-size: 1rem;
    }

    .drop-zone-subtitle {
      font-size: 0.875rem;
    }

    .drop-zone-hint {
      font-size: 0.8rem;
    }
  }
}
</style>
